import React, { useState } from 'react';
import { Search, Sparkles, Package, ArrowRight, Loader2, Heart, Check, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { client, ximilarFetch } from '../api/client';
import { supabase } from '../lib/supabase';

export const Catalog = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [packOpening, setPackOpening] = useState(false);
  const [packCards, setPackCards] = useState<any[]>([]);
  const [addingToWishlist, setAddingToWishlist] = useState<string | null>(null);
  const [addingToCollection, setAddingToCollection] = useState<string | null>(null);
  const [aiInterpretation, setAiInterpretation] = useState<string | null>(null);
  const [searchMode, setSearchMode] = useState<'ai' | 'basic'>('ai');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setPackCards([]);
    setAiInterpretation(null);

    try {
      if (searchMode === 'ai') {
        // Use AI natural language query
        const res = await ximilarFetch('/api/ai/query', {
          method: 'POST',
          body: JSON.stringify({ query: query.trim() }),
        });
        if (res.ok) {
          const data = await res.json();
          setResults(data.data?.cards || []);
          setAiInterpretation(data.data?.interpretation || null);
        } else {
          // Fallback to basic search if AI query fails
          const fallbackRes = await ximilarFetch(`/api/catalog/search?q=${encodeURIComponent(query)}`);
          if (fallbackRes.ok) {
            const data = await fallbackRes.json();
            setResults(data.data || []);
          }
        }
      } else {
        const res = await ximilarFetch(`/api/catalog/search?q=${encodeURIComponent(query)}`);
        if (res.ok) {
          const data = await res.json();
          setResults(data.data || []);
        }
      }
    } catch (e) {
      console.error("Search failed", e);
    } finally {
      setLoading(false);
    }
  };

  const openPack = async () => {
    setPackOpening(true);
    setResults([]);
    try {
      const res = await ximilarFetch('/api/pack');
      if (res.ok) {
        const data = await res.json();
        setPackCards(data.data?.cards || []);
      }
    } catch (e) {
      console.error("Pack open failed", e);
    } finally {
      setPackOpening(false);
    }
  };



  const addToCollection = async (card: any) => {
    setAddingToCollection(card.id || card.name);
    try {
      const { data: { user } } = await client.auth.getSession();
      if (!user) throw new Error("Please log in.");

      // Parse price
      let priceNum = 0;
      if (card.prices?.raw) {
        priceNum = parseFloat(card.prices.raw);
      } else if (typeof card.price === 'string') {
        priceNum = parseFloat(card.price.replace(/[^0-9.]/g, '')) || 0;
      } else if (typeof card.price === 'number') {
        priceNum = card.price;
      }

      const { error } = await supabase.from('cards').insert({
        user_id: user.id,
        name: card.name,
        set_name: card.set || card.setName,
        grade: "Raw",
        price: priceNum,
        image_url: card.image || card.image_url,
        status: 'collection',
        catalog_card_id: card.id || null
      });

      if (error) throw error;

    } catch (e: any) {
      console.error("Add to collection failed", e);
    } finally {
      setTimeout(() => setAddingToCollection(null), 1000);
    }
  };

  const addToWishlist = async (card: any) => {
    setAddingToWishlist(card.id || card.name);
    try {
      const { data: { user } } = await client.auth.getSession();
      if (!user) throw new Error("Please log in.");

      // 1. Get "My Wishlist"
      let { data: lists } = await supabase
        .from('wishlists')
        .select('id, name')
        .eq('user_id', user.id)
        .eq('name', 'My Wishlist');

      let targetList = lists?.[0];

      // 2. Create if missing
      if (!targetList) {
        const { data: newList, error } = await supabase
          .from('wishlists')
          .insert({ user_id: user.id, name: 'My Wishlist' })
          .select()
          .single();

        if (error) throw error;
        targetList = newList;
      }

      // 3. Add item
      if (targetList) {
        const { error: itemError } = await supabase
          .from('wishlist_items')
          .insert({
            wishlist_id: targetList.id,
            catalog_card_id: card.id || null,
            card_name: card.name,
            image_url: card.image || card.image_url
          });

        if (itemError) throw itemError;
      }
    } catch (e) {
      console.error("Add to wishlist failed", e);
    } finally {
      setTimeout(() => setAddingToWishlist(null), 1000);
    }
  };

  return (
    <div className="p-4 pt-14 space-y-8 pb-32 min-h-screen bg-obsidian">
      <header>
        <h1 className="text-2xl font-bold text-white font-display mb-2">Card Catalog</h1>
        <p className="text-slate-400 text-sm">Powered by Ximilar AI — All Major Sports</p>
      </header>

      {/* Search Section */}
      <section className="space-y-4">
        {/* Search Mode Toggle */}
        <div className="flex p-1 bg-white/[0.03] rounded-xl border border-white/[0.04]">
          <button
            type="button"
            onClick={() => setSearchMode('ai')}
            className={`flex-1 py-2 text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-all ${searchMode === 'ai'
              ? 'bg-copper-500/20 text-copper-400 border border-copper-400/30'
              : 'text-slate-400 border border-transparent'
              }`}
          >
            <Sparkles size={12} /> AI Search
          </button>
          <button
            type="button"
            onClick={() => setSearchMode('basic')}
            className={`flex-1 py-2 text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-all ${searchMode === 'basic'
              ? 'bg-copper-500/20 text-copper-400 border border-copper-400/30'
              : 'text-slate-400 border border-transparent'
              }`}
          >
            <Search size={12} /> Name Search
          </button>
        </div>

        <form onSubmit={handleSearch} className="relative">
          {searchMode === 'ai' ? (
            <Sparkles className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-copper-400" />
          ) : (
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-copper-400" />
          )}
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchMode === 'ai' ? "Ask AI: 'Show me rookie cards from 2023'..." : "Search by card name..."}
            className="w-full glass rounded-xl py-4 pl-10 pr-12 text-white placeholder-slate-500 focus:outline-none focus:border-copper-400/50 transition-colors"
          />
          <button
            type="submit"
            disabled={loading}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-copper-500 rounded-lg text-white disabled:opacity-50"
          >
            {loading ? <Loader2 className="animate-spin w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
          </button>
        </form>

        {/* AI Interpretation Banner */}
        {aiInterpretation && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-xl p-3 flex items-start gap-2 border border-copper-400/20"
          >
            <Sparkles className="w-4 h-4 text-copper-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-slate-300">{aiInterpretation}</p>
          </motion.div>
        )}

        {/* Pack Opening Button */}
        <button
          onClick={openPack}
          disabled={packOpening}
          className="w-full py-4 glass-dark border border-copper-400/30 rounded-xl flex items-center justify-center gap-3 group hover:bg-copper-500/10 transition-colors"
        >
          <Package className={`w-5 h-5 text-copper-400 ${packOpening ? 'animate-bounce' : ''}`} />
          <span className="font-bold text-white">Simulate Pack Opening</span>
        </button>
      </section>

      {/* Results Grid */}
      <div className="grid grid-cols-2 gap-4">
        <AnimatePresence mode="popLayout">
          {/* Search Results */}
          {results.map((card, i) => (
            <motion.div
              key={card.id || i}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass rounded-xl overflow-hidden p-3 relative group"
            >
              <div className="aspect-[3/4] bg-slate-800 rounded-lg mb-3 overflow-hidden">
                <div className="w-full h-full flex items-center justify-center text-slate-600 text-xs">
                  {(card.image || card.image_url) ? (
                    <img
                      src={card.image || card.image_url}
                      alt={card.name || 'Card image'}
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : "No Image"}
                </div>
              </div>
              <h3 className="font-bold text-white text-sm truncate">{card.name}</h3>
              <div className="flex items-center justify-between mt-1">
                <div className="flex flex-col">
                  <span className="text-xs text-slate-400 truncate max-w-[100px]">{card.set || card.setName}</span>
                  {(card.prices?.raw || card.price) && (
                    <span className="text-xs font-bold text-green-400">
                      {card.prices?.raw ? `$${card.prices.raw}` : card.price}
                    </span>
                  )}
                </div>
                {card.hasParallels && (
                  <span className="flex items-center gap-1 text-[10px] text-yellow-500 bg-yellow-500/10 px-1.5 py-0.5 rounded border border-yellow-500/20">
                    <Sparkles size={8} />
                    {card.parallelCount}
                  </span>
                )}
              </div>

              <div className="absolute top-2 right-2 flex flex-col gap-2">
                <button
                  onClick={() => addToWishlist(card)}
                  className="p-2 bg-black/50 backdrop-blur rounded-full text-white hover:bg-red-500 hover:text-white transition-colors"
                >
                  {addingToWishlist === (card.id || card.name) ? <Check size={14} /> : <Heart size={14} />}
                </button>
                <button
                  onClick={() => addToCollection(card)}
                  className="p-2 bg-black/50 backdrop-blur rounded-full text-white hover:bg-blue-500 hover:text-white transition-colors"
                >
                  {addingToCollection === (card.id || card.name) ? <Check size={14} /> : <Plus size={14} />}
                </button>
              </div>
            </motion.div>
          ))}

          {/* Pack Cards */}
          {packCards.map((card, i) => (
            <motion.div
              key={card.id || i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`glass rounded-xl overflow-hidden p-3 border ${card.isParallel ? 'border-yellow-500/50 shadow-lg shadow-yellow-500/20' : 'border-white/5'} relative`}
            >
              <div className="aspect-[3/4] bg-slate-800 rounded-lg mb-3 overflow-hidden relative">
                {(card.image_url || card.image) ? (
                  <img
                    src={card.image_url || card.image}
                    alt={card.name || 'Card image'}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-600 text-xs">No Image</div>
                )}
                {card.isParallel && (
                  <div className="absolute top-2 right-2 bg-yellow-500 text-black text-[10px] font-bold px-1.5 py-0.5 rounded">
                    PARALLEL
                  </div>
                )}
              </div>
              <h3 className="font-bold text-white text-sm truncate">{card.name}</h3>
              <p className="text-xs text-slate-400 truncate">{card.set_name || card.parallelName || "Base Card"}</p>
              {card.numberedTo && (
                <p className="text-[10px] text-yellow-400 font-bold mt-1">/{card.numberedTo}</p>
              )}

              <div className="absolute bottom-3 right-3 flex flex-col gap-2">
                <button
                  onClick={() => addToWishlist(card)}
                  className="p-2 bg-black/50 backdrop-blur rounded-full text-white hover:bg-red-500 hover:text-white transition-colors"
                >
                  {addingToWishlist === (card.id || card.name) ? <Check size={14} /> : <Heart size={14} />}
                </button>
                <button
                  onClick={() => addToCollection(card)}
                  className="p-2 bg-black/50 backdrop-blur rounded-full text-white hover:bg-blue-500 hover:text-white transition-colors"
                >
                  {addingToCollection === (card.id || card.name) ? <Check size={14} /> : <Plus size={14} />}
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};
